import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearAllTourStates,
  deleteTourState,
  getAllTourStates,
  upsertTourState,
} from '../api/api';
import type { TourState } from '../types';

// ─── Query key factory ──────────────────────────────────────────────────────

export const tourStateKeys = {
  all: () => ['tour-states'] as const,
};

// ─── Cache mutators ─────────────────────────────────────────────────────────

const replaceInCache = (
  prev: TourState[] | null | undefined,
  next: TourState,
): TourState[] => {
  const others = (prev ?? []).filter(
    (s) => !(s.tourId === next.tourId && s.version === next.version),
  );
  return [...others, next];
};

const removeFromCache = (
  prev: TourState[] | null | undefined,
  tourId: string,
  version: number,
): TourState[] =>
  (prev ?? []).filter((s) => !(s.tourId === tourId && s.version === version));

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch every tour state for the authenticated user. Single source of
 * truth for "has the user seen this tour?" decisions across the app.
 *
 * ``data`` is ``TourState[] | null`` — ``null`` means the fetch ran while
 * the user was unauthenticated, so the backend state is still unknown.
 * The provider refetches in that case; see ``TourProvider``.
 *
 * Long ``staleTime`` because tour state changes only via this client's
 * own mutations — the optimistic ``setQueryData`` calls in those
 * mutations keep the cache in sync without us needing to refetch.
 */
export function useTourStates() {
  return useQuery({
    queryKey: tourStateKeys.all(),
    queryFn: getAllTourStates,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Upsert a single tour state. ``onMutate`` updates the cache
 * synchronously so any ``hasSeen`` reads on the *very next render* see
 * the new state — without this, the provider's auto-start checks could
 * race the network round-trip and re-launch a tour that just finished.
 *
 * On error we fall back to whatever was in the cache before the
 * optimistic update so the UI doesn't lie about a save that didn't
 * actually land.
 */
export function useUpsertTourStateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertTourState,
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: tourStateKeys.all() });
      const prev = queryClient.getQueryData<TourState[] | null>(tourStateKeys.all());
      queryClient.setQueryData<TourState[] | null>(tourStateKeys.all(), (old) =>
        replaceInCache(old, next),
      );
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(tourStateKeys.all(), ctx.prev);
      }
    },
    onSuccess: (saved) => {
      // Reconcile with the server's authoritative copy (server-stamped
      // ``updated_at``, etc.). The cached row might differ slightly from
      // the optimistic one we wrote in ``onMutate``.
      queryClient.setQueryData<TourState[] | null>(tourStateKeys.all(), (old) =>
        replaceInCache(old, saved),
      );
    },
  });
}

/** Delete a single tour state. Optimistic + roll-back on error. */
export function useDeleteTourStateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tourId, version }: { tourId: string; version: number }) =>
      deleteTourState(tourId, version),
    onMutate: async ({ tourId, version }) => {
      await queryClient.cancelQueries({ queryKey: tourStateKeys.all() });
      const prev = queryClient.getQueryData<TourState[] | null>(tourStateKeys.all());
      queryClient.setQueryData<TourState[] | null>(tourStateKeys.all(), (old) =>
        removeFromCache(old, tourId, version),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(tourStateKeys.all(), ctx.prev);
      }
    },
  });
}

/** Clear every tour state for the user. Optimistic + roll-back on error. */
export function useClearAllTourStatesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearAllTourStates,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: tourStateKeys.all() });
      const prev = queryClient.getQueryData<TourState[] | null>(tourStateKeys.all());
      queryClient.setQueryData<TourState[]>(tourStateKeys.all(), []);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(tourStateKeys.all(), ctx.prev);
      }
    },
  });
}
